import {
  Breadcrumbs,
  Avatar,
  Box,
  Card,
  Divider,
  IconButton,
  Typography,
  Link,
  Button,
  Checkbox,
} from "@mui/joy";
import {
  useMakeUserUpdateMutation,
  useStartDirectConversationMutation,
  useUserBySlugQuery,
  useUserMeQuery,
} from "../../__generated__/types-and-hooks";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "../../i18n";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import DOMPurify from "dompurify";
import TeamLinkButton from "../dash/organizations/TeamLinkButton";
import FileUploadButton from "../../components/FileUploadButton";
import CreateOutlinedIcon from "@mui/icons-material/CreateOutlined";
import HomeIcon from "@mui/icons-material/Home";
import { Link as RouterLink } from "react-router-dom";
import SendIcon from "@mui/icons-material/Send";
import { useCallback } from "react";

export default function UserProfile() {
  const t = useTranslation();
  const navigate = useNavigate();
  const [makeUserUpdateMutation] = useMakeUserUpdateMutation();
  const [startDirectConversationMutation] =
    useStartDirectConversationMutation();
  const { slug } = useParams();
  const userBySlugQuery = useUserBySlugQuery({ variables: { slug: slug! } });
  const user = userBySlugQuery.data?.user_by_slug;
  const userMeQuery = useUserMeQuery();
  const userMe = userMeQuery.data?.user_me;
  const editable = userMe?.slug === user?.slug;

  const handleStartConversation = useCallback(() => {
    if (!user) return;

    startDirectConversationMutation({
      variables: {
        conversation: {
          recipientId: user.id!,
        },
      },
      onCompleted() {
        navigate("/messages");
      },
    });
  }, [startDirectConversationMutation, navigate, user]);

  return (
    <>
      <Breadcrumbs
        aria-label="breadcrumbs"
        sx={{
          "--Breadcrumbs-gap": "4px",
          m: 1,
        }}
        size="sm"
      >
        <Link component={RouterLink} color="neutral" to="/">
          <HomeIcon />
        </Link>
        <Typography level="title-sm">{t("Profile")}</Typography>
      </Breadcrumbs>

      <Box sx={{ padding: 4, maxWidth: 900, margin: "0 auto" }}>
        {editable && user && (
          <Checkbox
            label={t("Show in Search & Network")}
            size="md"
            variant="outlined"
            checked={user.public}
            onChange={(e) => {
              makeUserUpdateMutation({
                variables: {
                  user: {
                    id: user.id,
                    public: e.target.checked,
                  },
                },
                onCompleted() {
                  userBySlugQuery.refetch();
                },
              });
            }}
          />
        )}
        <Card
          variant="outlined"
          sx={{
            display: "flex",
            flexDirection: "column",
            padding: 0,
            marginBottom: 3,
            boxShadow: "0px 0px 3px #000000",
            background:
              "radial-gradient(circle at 50% 50%, rgb(0 0 0) 20%, transparent 80%), conic-gradient(from 45deg at 50% 50%, rgb(5 2 33) 0%, rgb(11 17 43) 25%, rgb(2 6 33) 50%, rgb(13 41 51) 75%, rgb(7 2 33) 100%)",
          }}
        >
          <Box
            sx={{
              position: "relative",
              height: 220,
              width: "100%",
            }}
          >
            {user?.background && (
              <img
                src={user?.background ?? ""}
                alt="Background"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: 8,
                  borderBottomLeftRadius: 0,
                  borderBottomRightRadius: 0,
                }}
              />
            )}
            {editable && user && (
              <Box sx={{ position: "absolute", top: 20, right: 20 }}>
                <FileUploadButton
                  isPublic
                  onSuccess={async ([file]) => {
                    makeUserUpdateMutation({
                      variables: {
                        user: {
                          id: user.id,
                          background:
                            "https://app.qcsku.com/" + (file as any).key,
                        },
                      },
                      onCompleted() {
                        userBySlugQuery.refetch();
                      },
                    });
                  }}
                />
              </Box>
            )}
          </Box>
          <Avatar
            sx={{
              position: "absolute",
              width: 200,
              height: 200,
              marginBottom: 2,
              boxShadow: "lg",
              marginTop: 0,
              top: 50,
              left: 20,
              border: 2,
              borderColor: "var(--joy-palette-background-level1)",
            }}
            src={user?.picture ?? "/images/qcsku_light.jpg"}
            alt={t("User Avatar")}
          />
          {editable && user && (
            <Box sx={{ position: "absolute", top: 210, left: 100 }}>
              <FileUploadButton
                isPublic
                onSuccess={async ([file]) => {
                  makeUserUpdateMutation({
                    variables: {
                      user: {
                        id: user.id,
                        picture: "https://app.qcsku.com/" + (file as any).key,
                      },
                    },
                    onCompleted() {
                      userBySlugQuery.refetch();
                    },
                  });
                }}
              />
            </Box>
          )}
          <Box
            gap={1}
            sx={{
              width: "100%",
              p: 2,
              display: "flex",
              flexDirection: "column",
              alignItems: "start",
            }}
          >
            <Typography
              level="h2"
              endDecorator={
                <>
                  {user?.verifiedEmail && (
                    <VerifiedOutlinedIcon color="primary" />
                  )}
                  {editable && (
                    <IconButton
                      sx={{ marginLeft: 2 }}
                      variant="soft"
                      color="primary"
                      onClick={() => navigate("./edit-name")}
                    >
                      <CreateOutlinedIcon />
                    </IconButton>
                  )}
                  {userMe && user && userMe?.id !== user?.id && (
                    <Button
                      sx={{ marginLeft: 2 }}
                      variant="solid"
                      color="primary"
                      startDecorator={<SendIcon />}
                      onClick={handleStartConversation}
                    >
                      {t("Message")}
                    </Button>
                  )}
                </>
              }
            >
              {user?.firstName && user.firstName.length > 0
                ? user.firstName
                : user?.slug}{" "}
              {user?.lastName && user.lastName.length > 0 ? user.lastName : ""}{" "}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", flexWrap: "wrap" }} gap={2} pl={2} pr={2}>
            {user?.teamEdges.map((teamEdge) => (
              <TeamLinkButton
                key={teamEdge.team.id}
                name={teamEdge.team.name}
                slug={teamEdge.team.slug}
                picture={teamEdge.team.picture}
                route={`/network/${teamEdge.team.slug}`}
              />
            ))}
          </Box>

          {/* About Section */}
          {user && user.bio && (
            <>
              <Divider />
              <Box
                sx={{
                  position: "relative",
                  alignItems: "left",
                  width: "100%",
                  p: 3,
                }}
              >
                <Typography
                  sx={{ marginTop: 1 }}
                  endDecorator={
                    editable && (
                      <IconButton
                        sx={{
                          position: "absolute",
                          right: 8,
                          top: 8,
                        }}
                        variant="soft"
                        color="primary"
                        onClick={() => navigate("./edit-about")}
                      >
                        <CreateOutlinedIcon />
                      </IconButton>
                    )
                  }
                >
                  <div
                    className="quill-content"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(user.bio),
                    }}
                  />
                </Typography>
              </Box>
            </>
          )}
        </Card>
      </Box>
      <Outlet />
    </>
  );
}
